import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ConnectivityTest = () => {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);

  const addResult = (test, result) => {
    setResults(prev => ({
      ...prev,
      [test]: result
    }));
  };

  const testConnectivity = async () => {
    setLoading(true);
    setResults({});

    // Test 1: Check current axios configuration
    addResult('axios-config', {
      status: 'info',
      message: 'Current axios configuration',
      details: {
        baseURL: axios.defaults.baseURL,
        headers: axios.defaults.headers.common,
        currentURL: window.location.href,
        hostname: window.location.hostname,
        port: window.location.port
      }
    });

    // Test 2: Test backend connectivity
    try {
      const response = await fetch('http://localhost:8000/api/auth/login/', {
        method: 'OPTIONS'
      });
      addResult('backend-connectivity', {
        status: 'success',
        message: `Backend reachable (Status: ${response.status})`,
        details: { status: response.status, statusText: response.statusText }
      });
    } catch (error) {
      addResult('backend-connectivity', {
        status: 'error',
        message: 'Backend not reachable',
        details: { error: error.message }
      });
    }

    // Test 3: Test login API with axios
    try {
      const response = await axios.post('/api/auth/login/', {
        email: 'admin@gcra.gov.za',
        password: 'admin123'
      });
      addResult('axios-login', {
        status: 'success',
        message: 'Axios login successful',
        details: { 
          status: response.status, 
          hasToken: !!response.data.token,
          user: response.data.user?.email 
        }
      });
    } catch (error) {
      addResult('axios-login', {
        status: 'error',
        message: 'Axios login failed',
        details: { 
          message: error.message,
          code: error.code,
          response: error.response?.data,
          url: error.config?.url,
          baseURL: error.config?.baseURL
        }
      });
    }

    // Test 4: Test direct fetch to backend
    try {
      const response = await fetch('http://localhost:8000/api/auth/login/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: 'admin@gcra.gov.za',
          password: 'admin123'
        })
      });
      
      const data = await response.json();
      addResult('direct-fetch', {
        status: response.ok ? 'success' : 'error',
        message: `Direct fetch ${response.ok ? 'successful' : 'failed'}`,
        details: { 
          status: response.status, 
          data: data,
          hasToken: !!data.token
        }
      });
    } catch (error) {
      addResult('direct-fetch', {
        status: 'error',
        message: 'Direct fetch failed',
        details: { error: error.message }
      });
    }

    // Test 5: Environment variables
    addResult('environment', {
      status: 'info',
      message: 'Environment configuration',
      details: {
        REACT_APP_API_URL: process.env.REACT_APP_API_URL,
        REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
        NODE_ENV: process.env.NODE_ENV,
        PUBLIC_URL: process.env.PUBLIC_URL
      }
    });

    setLoading(false);
  };

  useEffect(() => {
    testConnectivity();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return '#28a745';
      case 'error': return '#dc3545';
      case 'info': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  return (
    <div style={{ 
      maxWidth: '1000px', 
      margin: '2rem auto', 
      padding: '2rem',
      fontFamily: 'Arial, sans-serif'
    }}>
      <h1 style={{ textAlign: 'center', marginBottom: '2rem' }}>
        Frontend-Backend Connectivity Test
      </h1>

      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <button 
          onClick={testConnectivity}
          disabled={loading}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#FFD700',
            color: '#1a1a1a',
            border: 'none',
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'Testing...' : 'Run Tests Again'}
        </button>
      </div>

      <div style={{ display: 'grid', gap: '1rem' }}>
        {Object.entries(results).map(([test, result]) => (
          <div 
            key={test}
            style={{
              padding: '1.5rem',
              backgroundColor: 'white',
              border: `2px solid ${getStatusColor(result.status)}`,
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <h3 style={{ 
              margin: '0 0 1rem 0',
              color: getStatusColor(result.status),
              textTransform: 'capitalize'
            }}>
              {test.replace('-', ' ')} - {result.status.toUpperCase()}
            </h3>
            
            <p style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>
              {result.message}
            </p>
            
            {result.details && (
              <pre style={{
                backgroundColor: '#f8f9fa',
                padding: '1rem',
                borderRadius: '4px',
                fontSize: '0.9rem',
                overflow: 'auto',
                margin: 0
              }}>
                {JSON.stringify(result.details, null, 2)}
              </pre>
            )}
          </div>
        ))}
      </div>

      <div style={{ 
        marginTop: '2rem', 
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px'
      }}>
        <h3>Quick Fixes to Try:</h3>
        <ol>
          <li><strong>Check if backend is running:</strong> Go to <a href="http://localhost:8000/admin/" target="_blank" rel="noopener noreferrer">http://localhost:8000/admin/</a></li>
          <li><strong>Clear browser cache:</strong> Press Ctrl+Shift+R</li>
          <li><strong>Check browser console:</strong> Press F12 and look for errors</li>
          <li><strong>Restart frontend:</strong> Stop and restart npm start</li>
          <li><strong>Check proxy configuration:</strong> Verify craco.config.js proxy settings</li>
        </ol>
      </div>
    </div>
  );
};

export default ConnectivityTest;
